import type { AuthUser } from '@repo/shared-types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Auth store — the SINGLE source of truth for "who am I?" in the FE.
 *
 * Three states the rest of the app must handle:
 *   - `_hasHydrated === false` (initial paint, no localStorage read yet)
 *     → render a thin loading splash; do NOT branch on token/user.
 *   - token present + user present   → AUTHENTICATED.
 *   - both null                      → GUEST (signed out, or never signed in).
 *
 * Persistence strategy:
 *   - localStorage key `globaleserp-auth`.
 *   - `partialize` excludes `_hasHydrated`, `isRedirecting` and any
 *     future transient flags — these are runtime-only and would
 *     re-introduce stale-storage bugs if persisted.
 *   - `onRehydrateStorage` flips `_hasHydrated` AFTER the store
 *     rehydrates from disk. Consumers MUST gate on `_hasHydrated`
 *     before reading `token` / `user` (see `useInitAuth`).
 *
 * The token TTL is enforced server-side via session row expiry; the FE
 * just trusts whatever it has in memory. A `/me` on mount re-validates
 * the token and either refreshes `user` or triggers the 401 → /
 * login redirect.
 */
export interface AuthState {
  token: string | null;
  user: AuthUser | null;
  _hasHydrated: boolean;
  isRedirecting: boolean;

  setSession: (input: { token: string; user: AuthUser }) => void;
  updateUser: (user: AuthUser) => void;
  setHasHydrated: (value: boolean) => void;
  setRedirecting: (value: boolean) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      _hasHydrated: false,
      isRedirecting: false,

      setSession: ({ token, user }) =>
        set(() => ({
          token,
          user,
          // A successful setSession implicitly means re-auth happened;
          // reset the redirect guard so a future 401 can re-redirect.
          isRedirecting: false,
        })),

      updateUser: (user) => set(() => ({ user })),

      setHasHydrated: (value) => set(() => ({ _hasHydrated: value })),

      setRedirecting: (value) => set(() => ({ isRedirecting: value })),

      clearAuth: () =>
        set(() => ({
          token: null,
          user: null,
          isRedirecting: false,
        })),
    }),
    {
      name: 'globaleserp-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        // `state` is undefined when nothing was stored (first visit).
        // Either way, mark hydrated so guards stop showing the splash.
        state?.setHasHydrated(true);
        // If there was a token but the user object was lost somehow,
        // treat as fully logged out — defense in depth against corrupt
        // localStorage leaving us in a "token but no user" limbo.
        if (state?.token && !state.user) {
          state.clearAuth();
        }
      },
    },
  ),
);

/**
 * Selector helpers — using a selector at the call site avoids re-
 * subscribing every consumer to every field. Call these from inside
 * a component (they MUST run inside a React render for the hook
 * subscription to take effect).
 */
export const selectToken = (s: AuthState) => s.token;
export const selectUser = (s: AuthState) => s.user;
export const selectIsHydrated = (s: AuthState) => s._hasHydrated;
export const selectIsAuthed = (s: AuthState) => Boolean(s._hasHydrated && s.token && s.user);
